/**
 * The CSS contract for a custom-background events page: the theme colors, the custom-property
 * names that carry them, and the single DOM applier both live previews use.
 *
 * This is the TypeScript mirror of `EventPageThemeVO` (PHP). The server render and the two live
 * previews — the event wizard's iframe and the events-calendar editor's iframe — must expose an
 * identical set of variables or a preview will disagree with the page it is previewing. Keeping
 * one applier here is what makes that true by construction rather than by discipline.
 *
 * Consumed by the `custom-bg-theme` SCSS mixin (server/assets/css/events/_custom-bg-theme.scss).
 */

import tinycolor from 'tinycolor2';
export interface EventPageTheme {
  /** Page background — also the card fill, per decision D8. */
  background: string;
  /** Derived legible text for that background. */
  text: string;
  /** Derived same-hue surface shade — the nearer of the two. */
  surface: string;
  /**
   * The further surface shade. The date tile's number band uses it, and it is the skeleton
   * shimmer's second stop: pulsing to the page colour instead would make the skeleton blink out.
   */
  surfaceAlt: string;
  /** Button/CTA fill. */
  accent: string;
  /** Button/CTA hover + active fill. */
  accentHover: string;
  /** Label color legible on `accent`. */
  buttonLabel: string;
}

/** Custom-property names, matching EventPageThemeVO::CSS_VAR_* exactly. */
export const EVENT_PAGE_THEME_CSS_VARS: Record<keyof EventPageTheme, string> = {
  background: '--event-page-bg',
  text: '--event-page-text',
  surface: '--event-page-surface',
  surfaceAlt: '--event-page-surface-alt',
  accent: '--event-page-accent',
  accentHover: '--event-page-accent-hover',
  buttonLabel: '--event-page-button-label',
};

/**
 * Label for a button whose fill is painted inline — the Mode A Register buttons.
 *
 * Not part of `EVENT_PAGE_THEME_CSS_VARS` because it is not a container variable: it is set on each
 * painted button, mirroring EventStyleVO::getActionLabelStyleForGivenColor so the live preview and
 * the server render agree. An inline `color` on the button cannot reach the label — the design
 * system paints it on `.btn-text` — which is why this exists at all.
 */
export const EVENT_PAGE_ACTION_LABEL_CSS_VAR = '--events-action-label';

/**
 * The action FILL. Mirror of `EventsVO::CSS_VAR_ACTION_FILL` — see that constant for why the ticket
 * checkout needs a variable rather than an inline paint, and why one variable covers both modes.
 */
export const EVENT_PAGE_ACTION_FILL_CSS_VAR = '--events-action-fill';

/** Hover/active counterpart of `EVENT_PAGE_ACTION_FILL_CSS_VAR`. Mirror of `EventsVO::CSS_VAR_ACTION_FILL_HOVER`. */
export const EVENT_PAGE_ACTION_FILL_HOVER_CSS_VAR = '--events-action-fill-hover';

/**
 * The page text as a bare `r, g, b` triplet, mirroring EventPageThemeVO::CSS_VAR_TEXT_RGB.
 *
 * Derived from `theme.text` and never stored, so it sits outside `EVENT_PAGE_THEME_CSS_VARS` — that map is
 * keyed by the stored `EventPageTheme` shape.
 *
 * A themed boundary is the page blended toward its own text, and neither SASS nor PostCSS can compute
 * that — both run at build time, while both operands only exist at runtime. Compositing the text over
 * the page with `rgba()` is the same result in CSS every browser already supports.
 */
export const EVENT_PAGE_TEXT_RGB_CSS_VAR = '--event-page-text-rgb';

/**
 * The state class the two chrome surfaces carry their own copy of, and the DOM hooks on each.
 * `EventEnd.php` emits the footer's; `EventHead.php` emits the nav's.
 */
const EVENTS_CHROME_CUSTOM_BG_CLASS = '-custom-bg';

/**
 * One chrome surface's DOM hooks. The two selectors are a pair — a surface and the wordmark inside it — so
 * they travel together rather than as loose positional arguments.
 */
interface EventsChromeSurface {
  selector: string;
  wordmarkSelector: string;
}

const EVENTS_FOOTER_SURFACE: EventsChromeSurface = {selector: '.js-events-footer', wordmarkSelector: '.js-events-footer-logo'};
const EVENTS_NAV_SURFACE: EventsChromeSurface = {selector: '.js-events-nav', wordmarkSelector: '.js-events-nav-logo'};

/** Where `EventEnd.php` and `EventHead.php` park both wordmark URLs so a preview can swap between them. */
const LOGO_FOR_LIGHT_BACKGROUND_ATTRIBUTE = 'data-logo-for-light-background';
const LOGO_FOR_DARK_BACKGROUND_ATTRIBUTE = 'data-logo-for-dark-background';

/** `#8c8c8c` -> `140, 140, 140`, the form `rgba()` needs when the channels arrive through a variable. */
const toRgbTriplet = (hexColor: string): string => {
  const {r, g, b} = tinycolor(hexColor).toRgb();
  return `${r}, ${g}, ${b}`;
};

/**
 * Opt out of custom-background theming for one element: it keeps its own light background and its
 * text/icons are reset to the design-system neutral. For surfaces that read as controls — a select,
 * a white form card — rather than as page content. Buttons inside it stay themed.
 * Honored by the `custom-bg-theme` SCSS mixin.
 */
export const KEEPS_WHITE_BG_CLASS = '-keeps-white-bg';

/**
 * Opt in to custom-background theming for a bespoke element that paints its own white/light card
 * without using a design-system background utility. Honored by the `custom-bg-theme` SCSS mixin.
 */
export const HAS_WHITE_BG_CLASS = '-has-white-bg';

/**
 * Opt a button out of the accent fill so it follows the page text instead, like a plain control.
 *
 * For chrome that happens to be built from a button: the events calendar's "Today" sits in a row
 * with a layout dropdown and two caret icons, all of which only take the page text colour, so the
 * accent fill made it the one coloured thing in the row. Honored by the `custom-bg-theme` SCSS mixin.
 */
export const FOLLOWS_PAGE_TEXT_CLASS = '-follows-page-text';

/**
 * The subset of a derived palette a page theme needs. Both the color library's
 * `EventPaletteResult` and the wizard's `EventColorScheme` satisfy it — the latter keeps its two
 * newest fields optional so events saved before this feature hydrate cleanly.
 */
export interface EventPageThemeSource {
  primary: string;
  dark: string;
  darker: string;
  light: string;
  lighter: string;
  background?: string | null;
  buttonTextColor?: string;
}

/**
 * Narrow a derived palette to a page theme. Returns null in Mode A (no custom background), where
 * every surface renders exactly as it did before this feature.
 */
export const getEventPageThemeFromPalette = (palette: EventPageThemeSource, buttonLabelFallback: string): EventPageTheme | null => {
  if (!palette.background) {
    return null;
  }
  return {
    background: palette.background,
    text: palette.dark,
    surface: palette.light,
    surfaceAlt: palette.lighter,
    accent: palette.primary,
    accentHover: palette.darker,
    buttonLabel: palette.buttonTextColor ?? buttonLabelFallback,
  };
};

/**
 * Paint the page background behind the container as well.
 *
 * The events nav sits outside the themed container and carries a bottom margin, so leaving the
 * document unthemed left that gap reading as a band of white above the page. The server does the same
 * thing via `EventPageThemeVO::getInlineBodyStyle`; this keeps the live preview matching it.
 *
 * Only the background. The text colour is deliberately not set here: the nav is themed in its own
 * right now (`applyEventPageThemeToNav`), which paints its text from the same theme, and a colour
 * inherited from the body would reach chrome that has no themed background to sit on.
 */
const applyPageBodyBackground = (container: HTMLElement, background: string | null): void => {
  const body = container.ownerDocument.body;
  if (background === null) {
    body.style.removeProperty('background-color');
    return;
  }
  body.style.backgroundColor = background;
};

/**
 * Paint a themed page container, or strip the theme when `theme` is null. Sets the background and
 * text directly plus every custom property the `custom-bg-theme` SCSS mixin reads, and toggles the
 * state class the mixin is scoped to.
 */
export const applyEventPageTheme = (container: HTMLElement, theme: EventPageTheme | null, stateClass: string): void => {
  applyPageBodyBackground(container, theme?.background ?? null);
  applyEventPageThemeVariables(container, theme, stateClass);
  paintThemeBackgroundAndText(container, theme);
};

/**
 * Theme the events footer inside a live preview, or strip the theme when `theme` is null.
 *
 * The footer renders as a SIBLING of the page container, so it gets neither the contract's rules nor
 * its custom properties — both reach downward only. `EventEnd.php` hands the server render its own
 * copy of the state class and the variables; a preview mutates markup that was rendered once, so it
 * has to hand the footer the same copy or the footer keeps the design-system neutrals over the user's
 * background — measured `ct-neutral-tertiary` on "Made with" at 1.85:1 on `#fad956`.
 */
export const applyEventPageThemeToFooter = (previewDocument: Document | null | undefined, theme: EventPageTheme | null): void => {
  applyEventPageThemeToChrome(previewDocument, EVENTS_FOOTER_SURFACE, theme);
};

/**
 * Theme the events nav inside a live preview, or strip the theme when `theme` is null.
 *
 * The nav renders ABOVE the page container, which puts it in the footer's position exactly: out of
 * reach of both the contract's rules and its custom properties. `EventHead.php` hands the server
 * render its own copy; this hands a preview the same one.
 *
 * Only the events-calendar preview needs it. The event wizard's preview renders the page with
 * `isIframe` true, which swaps the whole nav for an empty `#emptyNav` spacer — there is nothing to
 * theme there, so calling this from the wizard would be dead code rather than belt and braces.
 */
export const applyEventPageThemeToNav = (previewDocument: Document | null | undefined, theme: EventPageTheme | null): void => {
  applyEventPageThemeToChrome(previewDocument, EVENTS_NAV_SURFACE, theme);
};

/**
 * The shared body of the two above: find the chrome surface, give it the state class, the variables and
 * the paint, then put the right wordmark on it. One function because the nav and the footer are the same
 * problem — a surface outside the themed container that the server hands its own copy of the theme.
 */
const applyEventPageThemeToChrome = (previewDocument: Document | null | undefined, surface: EventsChromeSurface, theme: EventPageTheme | null): void => {
  const chrome = previewDocument?.querySelector<HTMLElement>(surface.selector);

  if (!chrome) {
    return;
  }
  applyEventPageThemeVariables(chrome, theme, EVENTS_CHROME_CUSTOM_BG_CLASS);
  paintThemeBackgroundAndText(chrome, theme);
  swapWordmarkForBackground(chrome, surface.wordmarkSelector, theme);
};

/**
 * The wordmark is an `<img>` at a fixed ink colour, so it is swapped rather than recoloured — the
 * dark-ink file disappears on a dark page and the all-white one on a light page. Both URLs ride on
 * the `<img>` as data attributes because a preview cannot build an asset URL client-side, and the
 * choice is the one `EventPageThemeVO::isBackgroundDark()` makes on the server.
 */
const swapWordmarkForBackground = (chrome: HTMLElement, wordmarkSelector: string, theme: EventPageTheme | null): void => {
  const wordmark = chrome.querySelector<HTMLImageElement>(wordmarkSelector);
  const isDarkPage = theme !== null && isEventPageThemeDark(theme);
  const wordmarkURL = wordmark?.getAttribute(isDarkPage ? LOGO_FOR_DARK_BACKGROUND_ATTRIBUTE : LOGO_FOR_LIGHT_BACKGROUND_ATTRIBUTE);

  if (!wordmark || !wordmarkURL) {
    return;
  }
  wordmark.src = wordmarkURL;
};

/**
 * Whether the page reads dark, so a fixed-colour asset on it needs its light variant. The mirror of
 * `EventPageThemeVO::isBackgroundDark()`, down to asking the palette instead of re-deciding: the text
 * is derived away from the background — lighter on a dark page, darker on a light one — so a text
 * lighter than its background is the derivation's own verdict on this page. See that method for what
 * a second threshold cost on 10 of 30 sampled backgrounds.
 */
const isEventPageThemeDark = (theme: EventPageTheme): boolean => tinycolor(theme.text).getLuminance() > tinycolor(theme.background).getLuminance();

/** The background and text paint, shared by a themed page container and the footer. */
const paintThemeBackgroundAndText = (element: HTMLElement, theme: EventPageTheme | null): void => {
  if (theme === null) {
    element.style.backgroundColor = '';
    element.style.color = '';
    return;
  }
  element.style.backgroundColor = theme.background;
  element.style.color = theme.text;
};

/** The variables and the state class, without the background/text paint. Split out so both paths share it. */
const applyEventPageThemeVariables = (element: HTMLElement, theme: EventPageTheme | null, stateClass: string): void => {
  if (theme === null) {
    element.classList.remove(stateClass);
    Object.values(EVENT_PAGE_THEME_CSS_VARS).forEach((cssVar) => {
      element.style.removeProperty(cssVar);
    });
    element.style.removeProperty(EVENT_PAGE_TEXT_RGB_CSS_VAR);
    return;
  }
  element.classList.add(stateClass);
  Object.entries(EVENT_PAGE_THEME_CSS_VARS).forEach(([themeKey, cssVar]) => {
    element.style.setProperty(cssVar, theme[themeKey as keyof EventPageTheme]);
  });
  element.style.setProperty(EVENT_PAGE_TEXT_RGB_CSS_VAR, toRgbTriplet(theme.text));
};
